<template>
    <div>
        <div v-if="loading" class="flex items-center justify-center py-24">
            <UIcon name="i-ph-spinner" class="animate-spin text-gray-400 text-2xl" />
        </div>

        <div v-else-if="error" class="bg-white rounded-xl ring-1 ring-black/5 shadow-sm p-10 text-center">
            <UIcon name="i-ph-warning-circle" class="text-red-500 text-4xl mb-3" />
            <p class="text-gray-700 text-lg">{{ error }}</p>
        </div>

        <div v-else-if="transport" class="space-y-6">
            <!-- Sticky header with title + actions -->
            <div class="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 bg-gray-50/90 backdrop-blur border-b border-gray-200">
                <div class="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div class="flex items-center gap-3">
                        <img class="size-10" src="@/assets/img/logo.png" alt="SAMU Liban" />
                        <div>
                            <h2 class="text-lg font-semibold text-gray-800">Transport #{{ transport.number?.toString().padStart(6, '0') }}</h2>
                            <div class="flex items-center gap-2 mt-0.5">
                                <BadgeTransportStatus :status="transport.status" />
                                <span class="text-xs text-gray-500">{{ tripTypeLabels[transport.trip_type] || transport.trip_type }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="transport.status === 'processing'" class="flex gap-2">
                        <UButton color="violet" size="lg" icon="i-ph-check-circle-fill" @click="showAccept = true">Accept</UButton>
                        <UButton color="red" variant="outline" size="lg" icon="i-ph-x-circle-fill" @click="showDecline = true">Decline</UButton>
                    </div>
                    <div v-else-if="transport.status === 'accepted'" class="flex gap-2">
                        <UButton color="emerald" size="lg" icon="i-ph-check-circle-fill" @click="showComplete = true">Complete</UButton>
                        <UButton color="red" variant="outline" size="lg" icon="i-ph-x-circle-fill" @click="showCancel = true">Cancel</UButton>
                    </div>
                    <div v-else class="text-sm text-gray-500">
                        <template v-if="transport.status === 'declined'">Declined</template>
                        <template v-else-if="transport.status === 'completed'">Completed</template>
                        <template v-else-if="transport.status === 'cancelled'">Cancelled</template>
                    </div>
                </div>
            </div>

            <!-- Map -->
            <div v-if="mapCenter" class="relative h-64 rounded-xl overflow-hidden ring-1 ring-black/5 shadow-sm">
                <Map
                    :center="mapCenter"
                    :markers="mapMarkers!"
                    :fit-bounds="true"
                    :controls="true"
                    :dark="false"
                />
                <div class="absolute top-3 left-3 z-10 bg-white/95 backdrop-blur rounded-md shadow ring-1 ring-black/5 px-3 py-2 text-xs space-y-1">
                    <div class="flex items-center gap-2">
                        <span class="inline-block size-3 rounded-full bg-emerald-500 ring-2 ring-white" />
                        <span class="text-gray-700 font-medium">Pickup</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <span class="inline-block size-3 rounded-full bg-red-500 ring-2 ring-white" />
                        <span class="text-gray-700 font-medium">Dropoff</span>
                    </div>
                </div>
            </div>

            <!-- Two column content -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Main column (2/3) -->
                <div class="lg:col-span-2 space-y-4">
                    <Section title="Patient">
                        <DataRow label="Name" :value="customerName" />
                        <DataRow label="Phone" :value="transport.customer?.phone" />
                        <DataRow label="Date of Birth" :value="transport.customer?.birth_date" />
                    </Section>

                    <Section title="Transport Details">
                        <DataRow label="Reason" :value="reasonLabels[transport.reason] || transport.reason" />
                        <DataRow v-if="transport.reason_details" label="Details" :value="transport.reason_details" />
                        <DataRow v-if="transport.appointment_time" label="Appointment Time" :value="transport.appointment_time" />
                        <DataRow v-if="transport.posture" label="Posture" :value="postureLabels[transport.posture] || transport.posture" />
                        <DataRow v-if="transport.mobility" label="Mobility" :value="mobilityLabels[transport.mobility] || transport.mobility" />
                        <DataRow label="Trip Type" :value="tripTypeLabels[transport.trip_type] || transport.trip_type" />
                    </Section>

                    <Section title="Pickup">
                        <template v-if="hasCoords(transport.departure_address)" #action>
                            <UButton
                                color="gray"
                                variant="outline"
                                size="xs"
                                icon="i-ph-copy"
                                @click="copyLocation(transport.departure_address)"
                            >Copy Location</UButton>
                        </template>
                        <DataRow label="Type" :value="transport.pickup_type ? (locationTypeLabels[transport.pickup_type] || transport.pickup_type) : ''" />
                        <template v-if="transport.pickup_type === 'medical_facility'">
                            <DataRow v-if="transport.departure_address?.name || transport.origin_hospital" label="Facility" :value="transport.departure_address?.name || transport.origin_hospital" />
                            <DataRow v-if="transport.departure_address?.department || transport.origin_department" label="Department" :value="transport.departure_address?.department || transport.origin_department" />
                            <DataRow v-if="transport.departure_address?.room || transport.origin_room" label="Room" :value="transport.departure_address?.room || transport.origin_room" />
                        </template>
                        <template v-else>
                            <DataRow v-if="transport.departure_address?.city" label="City" :value="transport.departure_address.city" />
                            <DataRow v-if="transport.departure_address?.street" label="Street" :value="transport.departure_address.street" />
                            <DataRow v-if="transport.departure_address?.building" label="Building" :value="transport.departure_address.building" />
                            <DataRow v-if="transport.departure_address?.floor" label="Floor" :value="transport.departure_address.floor" />
                            <DataRow v-if="addressFlagBadges(transport.departure_address).length" label="Access Notes">
                                <div class="flex flex-wrap justify-end gap-1.5">
                                    <UBadge
                                        v-for="flag in addressFlagBadges(transport.departure_address)"
                                        :key="flag"
                                        color="amber"
                                        variant="soft"
                                        :label="flag"
                                    />
                                </div>
                            </DataRow>
                        </template>
                        <DataRow v-if="transport.departure_address?.formatted" label="Full Address">
                            <AddressLines :value="transport.departure_address.formatted" />
                        </DataRow>
                    </Section>

                    <Section title="Dropoff">
                        <template v-if="hasCoords(transport.arrival_address)" #action>
                            <UButton
                                color="gray"
                                variant="outline"
                                size="xs"
                                icon="i-ph-copy"
                                @click="copyLocation(transport.arrival_address)"
                            >Copy Location</UButton>
                        </template>
                        <DataRow label="Type" :value="transport.dropoff_type ? (locationTypeLabels[transport.dropoff_type] || transport.dropoff_type) : ''" />
                        <template v-if="transport.dropoff_type === 'medical_facility'">
                            <DataRow v-if="transport.arrival_address?.name" label="Facility" :value="transport.arrival_address?.name" />
                            <DataRow v-if="transport.arrival_address?.department" label="Department" :value="transport.arrival_address?.department" />
                            <DataRow v-if="transport.arrival_address?.room" label="Room" :value="transport.arrival_address?.room" />
                        </template>
                        <template v-else>
                            <DataRow v-if="transport.arrival_address?.city" label="City" :value="transport.arrival_address.city" />
                            <DataRow v-if="transport.arrival_address?.street" label="Street" :value="transport.arrival_address.street" />
                            <DataRow v-if="transport.arrival_address?.building" label="Building" :value="transport.arrival_address.building" />
                            <DataRow v-if="transport.arrival_address?.floor" label="Floor" :value="transport.arrival_address.floor" />
                            <DataRow v-if="addressFlagBadges(transport.arrival_address).length" label="Access Notes">
                                <div class="flex flex-wrap justify-end gap-1.5">
                                    <UBadge
                                        v-for="flag in addressFlagBadges(transport.arrival_address)"
                                        :key="flag"
                                        color="amber"
                                        variant="soft"
                                        :label="flag"
                                    />
                                </div>
                            </DataRow>
                        </template>
                        <DataRow v-if="transport.arrival_address?.formatted" label="Full Address">
                            <AddressLines :value="transport.arrival_address.formatted" />
                        </DataRow>
                    </Section>
                </div>

                <!-- Sidebar (1/3) -->
                <div class="space-y-4">
                    <Section v-if="transport.estimated_fee != null || transport.price != null" title="Pricing">
                        <DataRow v-if="transport.estimated_fee != null" label="Estimated Fee" :value="`$${transport.estimated_fee}`" />
                        <DataRow v-if="transport.price != null" label="Final Price">
                            <span class="text-base font-bold font-mono text-emerald-600">${{ transport.price }}</span>
                        </DataRow>
                    </Section>

                    <Section title="Schedule">
                        <DataRow label="Date" :value="formatDate(transport.scheduled_date)" />
                        <DataRow label="Time" :value="transport.scheduled_time?.slice(0, 5)" />
                    </Section>

                    <Section title="Medical">
                        <DataRow label="Treating Doctor" :value="transport.treating_doctor" />
                        <DataRow label="Medical Report">
                            <UBadge
                                :color="transport.has_medical_report ? 'green' : 'gray'"
                                variant="soft"
                                size="xs"
                                :label="transport.has_medical_report ? 'Yes' : 'No'"
                            />
                        </DataRow>
                    </Section>

                    <Section v-if="transport.companion_name" title="Companion">
                        <DataRow label="Name" :value="transport.companion_name" />
                        <DataRow label="Phone" :value="transport.companion_phone" />
                        <DataRow v-if="transport.companion_relationship" label="Relationship" :value="transport.companion_relationship" />
                    </Section>

                    <Section v-if="transport.notes" title="Notes">
                        <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ transport.notes }}</p>
                    </Section>

                    <Section v-if="transport.status !== 'processing' && transport.remarks" title="Provider Remarks">
                        <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ transport.remarks }}</p>
                    </Section>
                </div>
            </div>
        </div>

        <TransportAcceptModal
            v-model="showAccept"
            :submitting="submitting"
            @submit="onAccept"
        />
        <TransportDeclineModal
            v-model="showDecline"
            :submitting="submitting"
            @submit="onDecline"
        />
        <TransportCompleteModal
            v-model="showComplete"
            :submitting="submitting"
            :initial-price="transport?.estimated_fee ?? null"
            @submit="onComplete"
        />
        <TransportCancelModal
            v-model="showCancel"
            :submitting="submitting"
            @submit="onCancel"
        />
    </div>
</template>

<script setup lang="ts">
import type { LngLatLike } from 'maplibre-gl'
import dayjs from 'dayjs'
import {
    TransportReason, TransportPosture, TransportMobility, TransportTripType,
    LocationType,
} from '~/types/transport'
import type { TransportAddress } from '~/types/transport'

definePageMeta({ layout: 'transport' })

const route = useRoute()
const toast = useToast()

const token = computed(() => route.query.token as string)
const loading = ref(true)
const error = ref('')
const transport = ref<any>(null)
const submitting = ref(false)

const showAccept = ref(false)
const showDecline = ref(false)
const showComplete = ref(false)
const showCancel = ref(false)

const reasonLabels: Record<string, string> = {
    [TransportReason.returnHome]: 'Return Home',
    [TransportReason.assessmentAppointment]: 'Assessment Appointment',
    [TransportReason.medicalVisit]: 'Medical Visit',
    [TransportReason.hospitalAppointment]: 'Hospital Appointment',
}

const postureLabels: Record<string, string> = {
    [TransportPosture.walksAlone]: 'Walks Alone',
    [TransportPosture.needsCane]: 'Needs a Cane',
    [TransportPosture.needsHelp]: 'Needs Help',
}

const mobilityLabels: Record<string, string> = {
    [TransportMobility.wheelchair]: 'Wheelchair',
    [TransportMobility.bedridden]: 'Completely Bedridden',
}

const tripTypeLabels: Record<string, string> = {
    [TransportTripType.oneWay]: 'One Way',
    [TransportTripType.roundTrip]: 'Round Trip',
}

const locationTypeLabels: Record<string, string> = {
    [LocationType.medicalFacility]: 'Medical Facility',
    [LocationType.residential]: 'Residential',
}

const customerName = computed(() => {
    const c = transport.value?.customer
    if (!c) return ''
    return [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(' ')
})

const mapMarkers = computed(() => {
    const dep = transport.value?.departure_address
    const arr = transport.value?.arrival_address
    if (!dep?.lat || !dep?.lng || !arr?.lat || !arr?.lng) return null
    return [
        { lngLat: [dep.lng, dep.lat] as LngLatLike, color: '#10b981' },
        { lngLat: [arr.lng, arr.lat] as LngLatLike, color: '#ef4444' },
    ]
})

const mapCenter = computed<LngLatLike | null>(() => {
    if (!mapMarkers.value || mapMarkers.value.length === 0) return null
    return mapMarkers.value[0].lngLat
})

function hasCoords(addr: TransportAddress | null | undefined): boolean {
    return !!(addr?.lat && addr?.lng)
}

async function copyLocation(addr: TransportAddress | null | undefined) {
    if (!addr?.lat || !addr?.lng) return
    const url = `https://www.google.com/maps/?q=${addr.lat},${addr.lng}`
    try {
        await navigator.clipboard.writeText(url)
        toast.add({ title: 'Location copied', color: 'green' })
    } catch {
        toast.add({ title: 'Failed to copy location', color: 'red' })
    }
}

function addressFlagBadges(addr: TransportAddress | null | undefined): string[] {
    if (!addr) return []
    return [
        addr.narrow_stairs ? 'Narrow stairs' : null,
        addr.no_electricity ? 'No electricity' : null,
        addr.no_elevator ? 'No elevator' : null,
    ].filter(Boolean) as string[]
}

function formatDate(d?: string | null) {
    return d ? dayjs(d).format('MMM D, YYYY') : ''
}

async function fetchTransport() {
    if (!token.value) {
        error.value = 'No token provided'
        loading.value = false
        return
    }

    try {
        transport.value = await $fetch(`/api/transports/public/${token.value}`)
    } catch (e: any) {
        error.value = e?.data?.message || 'This link is invalid or has expired.'
    } finally {
        loading.value = false
    }
}

async function respond(body: any, successTitle: string, successColor: 'violet' | 'red' | 'emerald') {
    submitting.value = true
    try {
        await $fetch('/api/transports/public/respond', { method: 'POST', body: { token: token.value, ...body } })
        showAccept.value = false
        showDecline.value = false
        showComplete.value = false
        showCancel.value = false
        await fetchTransport()
        toast.add({ title: successTitle, color: successColor })
    } catch (e: any) {
        toast.add({ title: e?.data?.message || 'Something went wrong', color: 'red' })
    } finally {
        submitting.value = false
    }
}

function onAccept(payload: { remarks: string | null }) {
    respond({ action: 'accept', remarks: payload.remarks }, 'Transport accepted', 'violet')
}

function onDecline(payload: { remarks: string | null }) {
    respond({ action: 'decline', remarks: payload.remarks }, 'Transport declined', 'red')
}

function onComplete(payload: { price: number; remarks: string | null }) {
    respond({ action: 'complete', price: payload.price, remarks: payload.remarks }, 'Transport completed', 'emerald')
}

function onCancel(payload: { remarks: string | null }) {
    respond({ action: 'cancel', remarks: payload.remarks }, 'Transport cancelled', 'red')
}

onMounted(fetchTransport)
</script>

<script lang="ts">
const Section = defineComponent({
    props: { title: { type: String, required: true } },
    setup(props, { slots }) {
        return () => h('div', { class: 'bg-white rounded-xl ring-1 ring-black/5 shadow-sm overflow-hidden' }, [
            h('div', { class: 'px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-2 min-h-[44px]' }, [
                h('h3', { class: 'text-xs font-semibold uppercase tracking-wider text-gray-500' }, props.title),
                slots.action?.(),
            ]),
            h('div', { class: 'p-5 space-y-2.5' }, slots.default?.()),
        ])
    },
})

const DataRow = defineComponent({
    props: {
        label: { type: String, required: true },
        value: { type: [String, Number], default: null },
    },
    setup(props, { slots }) {
        return () => {
            const hasSlot = !!slots.default
            const hasValue = props.value !== null && props.value !== undefined && props.value !== ''
            const valueNode = hasSlot
                ? h('dd', { class: 'text-sm text-right min-w-0' }, slots.default!())
                : hasValue
                    ? h('dd', { class: 'text-sm text-gray-800 text-right font-medium min-w-0' }, String(props.value))
                    : h('dd', { class: 'text-right' }, [
                        h(resolveComponent('UBadge'), { color: 'gray', variant: 'soft', size: 'xs', label: 'N/A' }),
                    ])
            return h('div', { class: 'flex items-start justify-between gap-4' }, [
                h('dt', { class: 'text-sm text-gray-500 shrink-0 pt-0.5' }, props.label),
                valueNode,
            ])
        }
    },
})

const AddressLines = defineComponent({
    props: { value: { type: String, required: true } },
    setup(props) {
        return () => {
            const lines = props.value.split(',').map(s => s.trim()).filter(Boolean)
            return h('div', { class: 'text-right font-medium text-gray-800 leading-snug' },
                lines.map(line => h('div', line))
            )
        }
    },
})
</script>
